import React, {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useRef,
    useState,
    useCallback
} from 'react'
import axios from 'axios'

import ChatbotPopup from 'src/ui/chatbot/ChatbotPopup'
import ChatbotButton from 'src/ui/chatbot//ChatbotButton'
import { useTran } from 'src/i18n/useTran'
import { msg } from 'src/i18n/texts/keys'
// import { useLanguageValue } from 'src/i18n/useLanguage'
import { getAppContext } from 'src/context/AppContext'
import { useGeolocation } from 'src/geo/locationService'
import { useInactivityTimers } from 'src/ui/chatbot/components/InactivityTimers'
import SystemBannerPortal, {
    SystemBannerPayload
} from 'src/ui/chatbot/components/SystemBannerPortal'
import { isMobileScreen } from 'src/ui/style/ScreenSize'

type StreamHandlers = {
    onStart?: () => void
    onToken?: (delta: string, full: string) => void
    onEnd?: (full: string, meta?: Record<string, unknown>) => void
    onError?: (err: unknown) => void
}

interface ChatbotContextType {
    isOpen: boolean
    openChatbot: () => void
    closeChatbot: () => void
    toggleChatbot: () => void

    isStreaming: boolean
    sendMessageStream: (
        message: string,
        handlers?: StreamHandlers
    ) => Promise<string>
    abortStreaming: () => void

    inactivity: {
        msToAutoClear: number
        warningActive: boolean
        cleared: boolean
    }
    systemBanner: SystemBannerPayload | null
    clearWarning: () => void
    markActivity: () => void
    isInputLocked: boolean
    dismissWarning: () => void
}

const ChatbotContext = createContext<ChatbotContextType | undefined>(undefined)
const minLenForLengthHeuristic = 40
const relThreshold = 0.8

export const useChatbot = (): ChatbotContextType => {
    const context = useContext(ChatbotContext)
    if (!context)
        throw new Error('useChatbot must be used within a ChatbotProvider')
    return context
}

interface ChatbotProviderProps {
    children: ReactNode
    title?: string
    showButton?: boolean
    buttonLabel?: string
}

// --- helpers ---
const genSessionId = () =>
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`

const getBrowserCoords = async (
    attemptTimeouts: number[] = [4000, 5000]
): Promise<{ lat: number; lng: number } | null> => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) return null
    const timeouts = attemptTimeouts.length
        ? attemptTimeouts.map(t => Math.max(500, Math.floor(t)))
        : [4000, 5000]

    for (let attempt = 0; attempt < timeouts.length; attempt++) {
        const perAttemptTimeout = timeouts[attempt]
        const coord = await new Promise<{
            lat: number
            lng: number
        } | null>(resolve => {
            const timer = setTimeout(() => resolve(null), perAttemptTimeout)
            navigator.geolocation.getCurrentPosition(
                pos => {
                    clearTimeout(timer)
                    resolve({
                        lat: pos.coords.latitude,
                        lng: pos.coords.longitude
                    })
                },
                err => {
                    clearTimeout(timer)
                    if (attempt === timeouts.length - 1) {
                        console.error(
                            `Geolocation error (${err.code}): ${err.message}`
                        )
                    }
                    resolve(null)
                },
                {
                    enableHighAccuracy: false,
                    timeout: perAttemptTimeout,
                    maximumAge: 60000
                }
            )
        })
        if (coord) return coord
        if (attempt < timeouts.length - 1) {
            await new Promise(res => setTimeout(res, 150))
        }
    }
    return null
}

export const ChatbotProvider: React.FC<ChatbotProviderProps> = ({
    children,
    showButton = true
}) => {
    const t = useTran()
    // const lang = useLanguageValue()
    const chatbotEnable = getAppContext().config.chatbotEnable
    const [isOpen, setIsOpen] = useState(false)
    const [isOpening, setIsOpening] = useState(false)
    const [mountNonce, setMountNonce] = useState(0)
    const [sessionId, setSessionId] = useState<string | null>(null)
    const [warningDismissed, setWarningDismissed] = useState(false)
    const [isInputLocked, setIsInputLocked] = useState(false)
    const geolocation = useGeolocation()
    const closingRef = useRef(false)
    const latestSidRef = useRef<string | null>(null)
    const geoResolvedRef = useRef(false)
    const prefetchedCoordsRef = useRef<Promise<{
        lat: number
        lng: number
    } | null> | null>(null)
    const geoResolvePromiseRef = useRef<Promise<void> | null>(null)
    const isMobile = typeof window !== 'undefined' ? isMobileScreen() : false

    const [systemBanner, setSystemBanner] =
        useState<SystemBannerPayload | null>(null)

    // streaming state/controls
    const [isStreaming, setIsStreaming] = useState(false)
    const currentAbortRef = useRef<AbortController | null>(null)

    if (!chatbotEnable) {
        console.warn('Chatbot is disabled in the configuration.')
        return <>{children}</>
    }

    const buildLocation = useCallback(() => {
        const hasCoords = Boolean(geolocation?.position?.coords)
        return hasCoords
            ? {
                  lat: geolocation!.position!.coords.latitude,
                  lng: geolocation!.position!.coords.longitude
              }
            : null
    }, [geolocation])

    const checkBackendHealth = useCallback(async (): Promise<boolean> => {
        try {
            const resp = await axios.get('/health', {
                timeout: 3000,
                validateStatus: () => true
            })
            if (resp.status !== 200) return false
            const d = resp.data ?? {}
            const baseOk = String(d?.status || '').toLowerCase() === 'ok'
            const chatOk =
                !('chat' in d) || String(d.chat).toLowerCase() === 'ok'
            const dbOk = !('db' in d) || String(d.db).toLowerCase() === 'ok'
            return baseOk && chatOk && dbOk
        } catch {
            return false
        }
    }, [])

    const loadInactivityConfig = useCallback(async () => {
        try {
            const resp = await axios.get('/chatbot/config', {
                timeout: 3000,
                validateStatus: () => true
            })
            if (resp.status !== 200) return

            const raw = resp.data ?? {}
            const maxIdleMs = Number(raw?.maxIdleMs)
            const warnMs = Number(raw?.warnMs)

            setInactCfg(prev => {
                const next = {
                    maxIdleMs:
                        Number.isFinite(maxIdleMs) && maxIdleMs > 0
                            ? maxIdleMs
                            : prev.maxIdleMs,
                    warnMs:
                        Number.isFinite(warnMs) && warnMs > 0
                            ? warnMs
                            : prev.warnMs
                }
                if (
                    next.maxIdleMs === prev.maxIdleMs &&
                    next.warnMs === prev.warnMs
                )
                    return prev
                return next
            })
        } catch {
            // keep defaults on failure
        }
    }, [])

    const resolveGeo = useCallback(
        async (
            sid: string,
            opts?: { attemptBudgets?: number[] }
        ) => {
            const attemptBudgets =
                (opts?.attemptBudgets?.length
                    ? opts.attemptBudgets
                    : [5000, 6000]) ?? [5000, 6000]

            for (let attempt = 0; attempt < attemptBudgets.length; attempt++) {
                const budget = Math.max(
                    800,
                    Math.floor(attemptBudgets[attempt] ?? 0)
                )
                const attemptStart = Date.now()

                const locBudget = Math.max(
                    800,
                    Math.min(
                        budget - 500,
                        attempt === 0 ? 4000 : 5000
                    )
                )

                const locPromise =
                    prefetchedCoordsRef.current ??
                    getBrowserCoords([
                        Math.min(locBudget, 4000),
                        Math.min(locBudget, 5000)
                    ])
                let loc = await locPromise
                if (!loc) loc = buildLocation()
                prefetchedCoordsRef.current = null

                const elapsed = Date.now() - attemptStart
                const remaining = Math.max(300, budget - elapsed)

                try {
                    await axios.post(
                        '/chatbot/geo/resolve',
                        { sessionId: sid, ...(loc || {}) },
                        { timeout: remaining }
                    )
                    geoResolvedRef.current = true
                    return
                } catch {
                    if (attempt < attemptBudgets.length - 1) {
                        await new Promise(res => setTimeout(res, 200))
                    }
                }
            }
        },
        [buildLocation]
    )

    const ensureGeoResolve = useCallback(
        async (sid: string) => {
            if (geoResolvedRef.current) return

            if (!geoResolvePromiseRef.current) {
                geoResolvePromiseRef.current = resolveGeo(sid, {
                    attemptBudgets: [5000, 6000]
                })
                    .catch(() => {})
                    .finally(() => {
                        geoResolvePromiseRef.current = null
                    })
            }

            await Promise.race([
                geoResolvePromiseRef.current,
                new Promise(res => setTimeout(res, 6500))
            ])
        },
        [resolveGeo]
    )

    // ————— session lifecycle —————
    const startSession = useCallback(async () => {
        const sid = genSessionId()
        setSessionId(sid)
        geoResolvedRef.current = false
        return sid
    }, [])

    const ensureSession = useCallback(async (): Promise<string> => {
        if (sessionIdRef.current) return sessionIdRef.current
        const sid = await startSession()
        latestSidRef.current = sid
        return sid
    }, [startSession])

    const endSession = useCallback(async (sid: string | null) => {
        if (!sid) return
        try {
            await axios.post(
                '/session/close',
                { sessionId: sid },
                { timeout: 5000 }
            )
            console.log('Chatbot session ended:', sid)
        } catch {
        } finally {
            setSessionId(null)
        }
    }, [])

    const abortStreaming = useCallback(() => {
        try {
            currentAbortRef.current?.abort()
        } catch {}
    }, [])

    const clearWarning = useCallback(() => setSystemBanner(null), [])
    const dismissWarning = useCallback(() => {
        setWarningDismissed(true)
        setSystemBanner(prev => (prev?.type === 'idle-warning' ? null : prev))
    }, [])
    const msToAutoClearRef = useRef(0)
    const markActivityRef = useRef<() => void>(() => {})

    const [inactCfg, setInactCfg] = useState({
        maxIdleMs: 60 * 60 * 1000,
        warnMs: 5 * 60 * 1000
    })

    const isOpenRef = useRef(isOpen)
    useEffect(() => {
        isOpenRef.current = isOpen
    }, [isOpen])

    const sessionIdRef = useRef<string | null>(null)
    useEffect(() => {
        sessionIdRef.current = sessionId
    }, [sessionId])

    useEffect(() => {
        loadInactivityConfig()
    }, [loadInactivityConfig])

    const autoClearedRef = useRef(false)
    useEffect(() => {
        if (sessionId) autoClearedRef.current = false
    }, [sessionId])

    const onAutoClear = useCallback(async () => {
        if (!isOpenRef.current || !sessionIdRef.current) return
        if (autoClearedRef.current) return
        autoClearedRef.current = true

        const id = sessionIdRef.current
        try {
            if (id) {
                await Promise.allSettled([
                    fetch('/session/close', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        keepalive: true,
                        body: JSON.stringify({ sessionId: id })
                    }),
                    fetch('/chatbot/geo/clear', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        keepalive: true,
                        body: JSON.stringify({ sessionId: id })
                    })
                ])
            }
        } finally {
            setSystemBanner(prev =>
                prev?.type === 'cleared' ? prev : { type: 'cleared' }
            )
            setSessionId(null)
        }
    }, [t])

    const isStreamingRef = useRef(isStreaming)
    useEffect(() => {
        isStreamingRef.current = isStreaming
    }, [isStreaming])

    const systemBannerRef = useRef<SystemBannerPayload | null>(null)
    useEffect(() => {
        systemBannerRef.current = systemBanner
    }, [systemBanner])

    const onIdleWarn = useCallback(() => {
        if (!isOpenRef.current || !sessionIdRef.current || warningDismissed)
            return
        if (isStreamingRef.current) return
        if (systemBannerRef.current?.type === 'error') return

        setSystemBanner(prev => {
            if (prev?.type === 'error' || prev?.type === 'idle-warning')
                return prev
            return { type: 'idle-warning', etaMs: msToAutoClearRef.current }
        })
    }, [warningDismissed])

    const {
        msToAutoClear,
        warningActive,
        cleared,
        markActivity,
        resetCleared
    } = useInactivityTimers(inactCfg, onIdleWarn, onAutoClear)

    msToAutoClearRef.current = msToAutoClear

    useEffect(() => {
        markActivityRef.current = markActivity
    }, [markActivity])

    const openChatbot = useCallback(async () => {
        if (isOpen || isOpening) return
        setWarningDismissed(false)
        setIsOpening(true)
        setSystemBanner(null)
        setIsInputLocked(true)
        if (!prefetchedCoordsRef.current) {
            prefetchedCoordsRef.current = getBrowserCoords([4000, 5000])
        }

        try {
            const healthy = await checkBackendHealth()
            if (!healthy) {
                setSystemBanner({
                    type: 'error',
                    message: msg.chatbot.chat.botunavailable.message
                })
                setIsInputLocked(false)
                setIsOpen(true)
                return
            }

            const sid = await ensureSession()
            latestSidRef.current = sid

            resetCleared?.()
            setIsOpen(true)
            setIsInputLocked(false)
            markActivity()

            void ensureGeoResolve(sid)
        } finally {
            setIsOpening(false)
        }
    }, [
        isOpen,
        isOpening,
        checkBackendHealth,
        ensureSession,
        markActivity,
        ensureGeoResolve,
        resetCleared
    ])

    const closeChatbot = useCallback(async () => {
        if (closingRef.current) return
        closingRef.current = true

        setIsOpen(false)
        abortStreaming()

        const sidToClose = latestSidRef.current || sessionId

        try {
            if (sidToClose) {
                await Promise.allSettled([
                    axios.post(
                        '/chatbot/geo/clear',
                        { sessionId: sidToClose },
                        { timeout: 3000 }
                    ),
                    axios.post(
                        '/session/close',
                        { sessionId: sidToClose },
                        { timeout: 5000 }
                    )
                ])
            }
        } catch {
        } finally {
            setSessionId(null)
            setIsInputLocked(false)
            latestSidRef.current = null
            geoResolvedRef.current = false
            prefetchedCoordsRef.current = null
            setSystemBanner(null)
            setWarningDismissed(false)
            resetCleared?.()
            setMountNonce(n => n + 1)
            closingRef.current = false
        }
    }, [abortStreaming, sessionId, resetCleared])

    useEffect(() => {
        latestSidRef.current = sessionId
    }, [sessionId])

    useEffect(() => {
        return () => {
            abortStreaming()
            if (sessionId) void endSession(sessionId)
        }
    }, [abortStreaming, endSession, sessionId])

    useEffect(() => {
        if (!sessionId || geoResolvedRef.current) return
        void ensureGeoResolve(sessionId)
    }, [sessionId, ensureGeoResolve])

    useEffect(() => {
        const sendCloseBeacon = () => {
            const sid = latestSidRef.current
            if (!sid) return

            const payload = JSON.stringify({ sessionId: sid })
            {
                const url = '/session/close'
                let ok = false
                try {
                    const blob = new Blob([payload], {
                        type: 'application/json'
                    })
                    ok =
                        typeof navigator.sendBeacon === 'function'
                            ? navigator.sendBeacon(url, blob)
                            : false
                } catch {
                    ok = false
                }

                if (!ok) {
                    try {
                        fetch(url, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: payload,
                            keepalive: true
                        })
                    } catch {}
                }
            }
            {
                const url = '/chatbot/geo/clear'
                let ok = false
                try {
                    const blob = new Blob([payload], {
                        type: 'application/json'
                    })
                    ok =
                        typeof navigator.sendBeacon === 'function'
                            ? navigator.sendBeacon(url, blob)
                            : false
                } catch {
                    ok = false
                }

                if (!ok) {
                    try {
                        fetch(url, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: payload,
                            keepalive: true
                        })
                    } catch {}
                }
            }
        }

        const onPageHide = () => {
            sendCloseBeacon()
        }
        const onBeforeUnload = () => {
            sendCloseBeacon()
        }

        window.addEventListener('pagehide', onPageHide)
        window.addEventListener('beforeunload', onBeforeUnload)

        return () => {
            window.removeEventListener('pagehide', onPageHide)
            window.removeEventListener('beforeunload', onBeforeUnload)
        }
    }, [])

    // ————— utilities —————

    const sendMessageStream = useCallback(
        async (message: string, handlers?: StreamHandlers): Promise<string> => {
            const messageText = (message || '').trim()
            if (!messageText) return ''

            const sid = sessionId ?? (await ensureSession())
            latestSidRef.current = sid

            await ensureGeoResolve(sid)

            const ac = new AbortController()
            currentAbortRef.current = ac

            handlers?.onStart?.()
            setIsStreaming(true)
            setWarningDismissed(false)
            markActivity()
            clearWarning()

            type PendingChunk =
                | { kind: 'token'; raw: string }
                | { kind: 'snapshot'; raw: string }
                | null

            let finalText = ''
            let pending: PendingChunk = null

            const norm = (s: string) => s.replace(/\r/g, '').normalize('NFC')
            const normWithTrim = (s: string) => norm(s).trim()

            const applyPending = () => {
                const p = pending
                if (!p) return

                if (p.kind === 'token') {
                    finalText += p.raw
                    handlers?.onToken?.(p.raw, finalText)
                } else {
                    const A = norm(finalText)
                    const B = norm(p.raw)
                    let i = 0
                    const n = Math.min(A.length, B.length)
                    while (i < n && A.charCodeAt(i) === B.charCodeAt(i)) i++
                    const delta = p.raw.slice(i)
                    if (delta) {
                        finalText = p.raw
                        handlers?.onToken?.(delta, finalText)
                    }
                }
                pending = null
            }

            try {
                const STREAM_URL = '/chatbot/stream'

                const body = {
                    question: messageText,
                    sessionId: sid
                }

                const resp = await fetch(STREAM_URL, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'text/event-stream'
                    },
                    body: JSON.stringify(body),
                    signal: ac.signal,
                    cache: 'no-store'
                })

                if (!resp.ok || !resp.body) {
                    throw new Error(`Stream start failed: ${resp.status}`)
                }

                const reader = resp.body.getReader()
                const decoder = new TextDecoder('utf-8')
                let buf = ''

                while (true) {
                    const { value, done } = await reader.read()
                    if (done) break
                    buf += decoder.decode(value, { stream: true })

                    const blocks = buf.split('\n\n')
                    buf = blocks.pop() ?? ''

                    for (const block of blocks) {
                        const lines = block.split('\n')
                        const dataLine = lines.find(l => l.startsWith('data:'))
                        if (!dataLine) continue

                        const jsonStr = dataLine.slice(5).trim()
                        if (!jsonStr) continue

                        let evt: any
                        try {
                            evt = JSON.parse(jsonStr)
                        } catch {
                            continue
                        }
                        if (pending && evt?.event !== 'end') {
                            applyPending()
                        }

                        if (evt?.event === 'end') {
                            if (pending) {
                                const p = pending
                                const A = normWithTrim(finalText)
                                const B = normWithTrim(p.raw)

                                const isRedundantLast =
                                    A.length > 0 && B.startsWith(A)

                                const isLargeRelativeDuplicate =
                                    A.length > minLenForLengthHeuristic - 1 &&
                                    p.kind === 'token' &&
                                    B.length >=
                                        Math.floor(A.length * relThreshold)

                                if (
                                    !isRedundantLast &&
                                    !isLargeRelativeDuplicate
                                ) {
                                    applyPending()
                                }
                                pending = null
                            }

                            const rawMeta = evt?.data?.meta
                            const meta =
                                rawMeta &&
                                typeof rawMeta === 'object' &&
                                !Array.isArray(rawMeta)
                                    ? (rawMeta as Record<string, unknown>)
                                    : undefined

                            handlers?.onEnd?.(finalText, meta)
                            markActivity()
                            setIsStreaming(false)
                            clearWarning()
                            currentAbortRef.current = null
                            return finalText
                        }

                        if (evt?.event === 'error') {
                            throw new Error(
                                evt?.data?.message || 'Upstream error'
                            )
                        }

                        if (evt?.event === 'token') {
                            const raw: string =
                                (evt?.data?.chunk as string) ||
                                (evt?.data?.delta as string) ||
                                (evt?.data?.token as string) ||
                                (evt?.data?.text as string) ||
                                ''

                            if (!raw) {
                                continue
                            }
                            if (pending) applyPending()

                            pending = { kind: 'token', raw }
                            continue
                        }

                        const d = evt?.data ?? evt
                        const snapshot: string =
                            (typeof d?.text === 'string' && d.text) ||
                            (typeof d?.message?.text === 'string' &&
                                d.message.text) ||
                            (typeof d?.output_text === 'string' &&
                                d.output_text) ||
                            ''

                        if (snapshot) {
                            if (pending) applyPending()
                            pending = { kind: 'snapshot', raw: snapshot }
                            markActivity()
                            continue
                        }
                    }
                }

                handlers?.onEnd?.(finalText, undefined)
                return finalText
            } catch (err) {
                const handled = Boolean(handlers?.onError)
                if ((err as any)?.name === 'AbortError') {
                    handlers?.onError?.(new Error('aborted'))
                } else {
                    handlers?.onError?.(err)
                }
                if (handled) {
                    return ''
                }
                throw err
            } finally {
                setIsStreaming(false)
                currentAbortRef.current = null
            }
        },
        [sessionId, ensureSession, clearWarning, markActivity, ensureGeoResolve]
    )

    return (
        <ChatbotContext.Provider
            value={{
                isOpen,
                openChatbot,
                closeChatbot,
                toggleChatbot: () =>
                    isOpen ? void closeChatbot() : void openChatbot(),
                isStreaming,
                sendMessageStream,
                abortStreaming,
                inactivity: { msToAutoClear, warningActive, cleared },
                systemBanner,
                clearWarning,
                markActivity,
                isInputLocked,
                dismissWarning
            }}
        >
            {children}

            {showButton && !isOpen && (
                <ChatbotButton
                    onClick={openChatbot}
                    label={t(msg.chatbot.popup.button)}
                />
            )}

            {isOpen ? (
                <ChatbotPopup key={mountNonce} isOpen onClose={closeChatbot} />
            ) : null}

            <SystemBannerPortal
                banner={isOpen && isMobile ? systemBanner : null}
                onDismiss={() => {
                    setSystemBanner(null)
                    setWarningDismissed(true)
                    setIsInputLocked(false)
                }}
            />
        </ChatbotContext.Provider>
    )
}

// Chatbot Hook Component - for easy usage in any component
interface UseChatbotButtonProps {
    label?: string
}

export const ChatbotTrigger: React.FC<UseChatbotButtonProps> = ({ label }) => {
    const { openChatbot } = useChatbot()
    return (
        <button
            onClick={openChatbot}
            style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center'
            }}
        >
            {label || (
                <>
                    <span style={{ marginRight: '5px' }}>💬</span>
                    Chat with us
                </>
            )}
        </button>
    )
}
